import { Link, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Dispatch, SetStateAction } from "react";
import { IconComponent } from "src/shared/components";
import { PartnerLabel } from "src/shared/components/PartnerLabel";
import { ERP_URL } from "src/shared/constants/app.constants";
import {
  advertisingServiceLogos,
  agencyLogos,
} from "src/shared/constants/company.constants";
import { getUrl } from "src/shared/helpers/url.helpers";
import { EIconNames } from "src/shared/types/iconNames";
import { EditableSpendsCell } from "../../ui/components/EditableSpendsCell";
import { IDraftReportItem } from "../types";

export const getColumns = (
  handleAllAdjustmentsChange: (id: number, value: string) => void,
  setAllAdjustedSpends: Dispatch<
    SetStateAction<{ id: number; spends: string }[]>
  >,
  refetch: () => void,
): GridColDef<IDraftReportItem>[] => [
  {
    headerName: "Партнер",
    type: "string",
    field: "partner_name",
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <PartnerLabel
        name={params.row.partner_name}
        organizationForm={params.row.organization_form?.value}
      />
    ),
  },
  {
    headerName: "Компания",
    sortable: false,
    field: "agency",
    flex: 1,
    minWidth: 110,
    cellClassName: "vertical-align-center",
    renderCell: (params) => {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconComponent
            sx={{ minWidth: 16, width: 16, height: 16, marginRight: 2 }}
            iconname={agencyLogos[params.row.agency.value]}
          />
          <Typography>{params.row.agency.name}</Typography>
        </Stack>
      );
    },
  },
  {
    headerName: "Виртуальный договор",
    sortable: false,
    type: "string",
    field: "contract",
    flex: 1,
    minWidth: 120,
  },
  {
    headerName: "Сервис",
    sortable: false,
    field: "service",
    flex: 1,
    minWidth: 100,
    cellClassName: "center-item",
    renderCell: (params) => {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconComponent
            sx={{ minWidth: 16, width: 16, height: 16, marginRight: 2 }}
            iconname={
              advertisingServiceLogos[params.row.service.value] ||
              EIconNames.WEBCOM_LOGO_SHORT
            }
          />
        </Stack>
      );
    },
  },
  {
    headerName: "Клиент партнера",
    type: "string",
    field: "client_name",
    flex: 1,
    minWidth: 150,
  },
  {
    headerName: "Сайт",
    sortable: false,
    type: "string",
    field: "site",
    flex: 1,
    minWidth: 100,
    renderCell: (params) => {
      return (
        <Link target="_blank" href={getUrl(params.row.site)}>
          {params.row.site}
        </Link>
      );
    },
  },
  {
    headerName: "ID ак-та клиента",
    sortable: false,
    field: "account_id",
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      return (
        <Link
          underline="hover"
          target="_blank"
          href={`${ERP_URL}/crm/project/${params.row.client_id}/${params.row.project_id}/?settings`}
        >
          {params.row.account_id}
        </Link>
      );
    },
  },
  {
    headerName: "Логин ак-та клиента",
    sortable: false,
    field: "account_login",
    flex: 1,
    minWidth: 150,
    renderCell: (params) => {
      return (
        <Link
          target="_blank"
          href={`${ERP_URL}/crm/project/${params.row.client_id}/${params.row.project_id}/?settings`}
        >
          {params.row.account_login}
        </Link>
      );
    },
  },
  {
    headerName: "Валюта ак-та",
    sortable: false,
    type: "string",
    field: "service_currency",
    flex: 1,
    minWidth: 80,
    cellClassName: "center-item",
  },
  {
    headerName: "Открутка Клиента в валюте ак-та",
    field: "spends_adjustment",
    flex: 1,
    minWidth: 170,
    cellClassName: "vertical-align-center center-item",
    renderCell(params) {
      return params.row.commited ? (
        `${params.row.spends_adjustment} ${params.row.service_currency}`
      ) : (
        <EditableSpendsCell
          key={params.row.spends_adjustment}
          id={params.row.id}
          spendsAdjustment={params.row.spends_adjustment}
          spends={params.row.spends}
          serviceCurrency={params.row.service_currency}
          handleAllAdjustmentsChange={handleAllAdjustmentsChange}
          refetch={refetch}
          setAllAdjustedSpends={setAllAdjustedSpends}
        />
      );
    },
  },
  {
    headerName: "Бонус Клиента в валюте ак-та",
    sortable: false,
    type: "string",
    field: "client_reward_to_pay_in_service_currency",
    valueFormatter: (v, row) => `${v} ${row.service_currency}`,
    flex: 1,
    minWidth: 110,
  },
  {
    headerName: "% бонуса Клиента",
    sortable: false,
    type: "string",
    field: "client_reward_percent",
    valueFormatter: (v) => `${v}%`,
    flex: 1,
    minWidth: 80,
  },
  {
    headerName: "Бонус по отк-ке Партнера в Валюте ак-та",
    sortable: false,
    type: "string",
    field: "partner_reward_in_service_currency",
    valueFormatter: (v, row) => `${v} ${row.service_currency}`,
    flex: 1,
    minWidth: 150,
  },
  {
    headerName: "% бонуса Партнера",
    sortable: false,
    type: "string",
    field: "partner_reward_percent",
    valueFormatter: (v) => `${v}%`,
    flex: 1,
    minWidth: 80,
  },
  {
    headerName: "Бонус к выплате Партнеру в валюте ак-та",
    sortable: false,
    type: "string",
    field: "partner_reward_to_pay_in_service_currency",
    valueFormatter: (v, row) => `${v} ${row.service_currency}`,
    flex: 1,
    minWidth: 150,
  },
  {
    headerName: "Бонус к выплате Партнеру в валюте КДА",
    sortable: false,
    type: "string",
    field: "partner_reward_to_pay_in_contract_currency",
    valueFormatter: (v, row) => `${v} ${row.contract_currency}`,
    flex: 1,
    minWidth: 150,
  },
];
